import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './style.css';
import { useSelector, useDispatch } from 'react-redux';

import { clientsSelector } from '../../store/slices/clients';
import { showLoading, hideLoading } from '../../store/slices/loadingState';

import textRatingToNumber from '../Client/ReviewsSection/services/textRatingToNumber';

import {
  API_REVIEW_UPDATE_MSG_REPLY,
  API_GET_GOOGLE_REVIEWS_V2,
  API_GET_YELP_REVIEWS,
  API_REVIEWS_HANDLER,
  API_REVIEW_APPROVE,
  REVIEWS_GENERATE_REPLY_BY_AI
} from '../../utils';

import { accountManagers } from '../../utils';

import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';

import {
  Star,
  Filter,
  User,
  Clock,
  RotateCcw,
  Pencil,
  Trash2,
  Check,
  CheckCheck,
  X,
  Search,
  Loader2
} from 'lucide-react';
import { TruncatedText } from '../../components/TruncatedText';
import { Checkbox } from '../../components/ui/checkbox';

const Actions = ({ row, refresh, setLoading }) => {
  const reviews = useSelector((state) => state.reviews);
  const dispatch = useDispatch();

  const handleCancel = async () => {
    dispatch({
      type: 'reviews/setEditable',
      payload: { id: row.id, editable: false }
    });
  };

  const handleClick = async () => {
    if (reviews?.editable?.id === row.id && reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: false }
      });

      if (
        reviews?.editable?.message &&
        reviews?.editable?.message.length > 0 &&
        reviews?.editable?.message !== row.msgReply
      ) {
        dispatch({
          type: 'reviews/save',
          payload: {
            loading: true,
            reviews: reviews?.reviews.map((review) =>
              review.id === row.id
                ? { ...review, msgReply: reviews?.editable?.message }
                : review
            )
          }
        });

        try {
          const response = await axios.post(`${API_REVIEW_UPDATE_MSG_REPLY}/`, {
            id: row.id,
            msgReply: reviews?.editable?.message
          });

          if (response.status === 200) {
            dispatch({ type: 'reviews/save', payload: { loading: false } });
          }
        } catch (err) {
          console.error(err);
        }
      }
    } else if (!reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: true, message: row.msgReply }
      });
    }
  };

  const handleApproved = async (value) => {
    try {
      const response = await axios.post(`${API_REVIEW_APPROVE}/`, {
        id: row.reviewId
      });

      if (response.status === 200) {
        dispatch({
          type: 'reviews/approve',
          payload: reviews?.reviews.map((review) =>
            review.id === row.id ? { ...review, checked: value } : review
          )
        });
      }
    } catch (err) {
      console.error('Error approving review:', err);
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: 'Delete Review',
      text: 'Are you sure you want to delete?',
      confirmButtonText: 'Yes',
      showCancelButton: true
    }).then((res) => {
      if (res.value) {
        setLoading(true);
        dispatch(showLoading());
        axios
          .post(API_REVIEWS_HANDLER, {
            review: {
              reviewId: row.reviewId,
              locationId: row.locationId,
              reviewer: row.reviewer,
              starRating: row.starRating
            },
            request: { sentBy: 'SUITE', operation: 'deny' }
          })
          .then((response) => {
            dispatch(hideLoading());
            setLoading(false);
            refresh();

            if (response.status === 200) {
              Swal.fire({
                title: 'Success!',
                text: 'Review Deleted ✅',
                showCloseButton: true
              });
            } else {
              Swal.fire({
                title: 'Error!',
                text: 'Something went wrong ❌',
                showCloseButton: true
              });
            }
          });
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {reviews?.editable?.editable && reviews?.editable?.id === row.id ? (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700">
            <Check className="h-4 w-4" />
            Confirm
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive">
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </>
      ) : reviews?.loading ? (
        <span className="text-sm text-muted-foreground">Loading...</span>
      ) : row?.checked ? (
        <span className="text-sm text-muted-foreground">Approved</span>
      ) : (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start">
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            onClick={() => handleApproved(true)}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-green-50 hover:text-green-700"
            style={{
              color: '#16a34a',
              borderColor: '#16a34a !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Check className="h-4 w-4" />
            Approve
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-destructive/10"
            style={{
              color: '#ef4444',
              borderColor: '#ef4444 !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </>
      )}
    </div>
  );
};

const Answer = ({ cell, row, clientInfo }) => {
  const reviews = useSelector((state) => state.reviews);
  const editable =
    reviews?.editable?.editable && reviews?.editable?.id === row.id;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleEditMessage = (msg) => {
    dispatch({ type: 'reviews/editMessage', payload: msg });
  };

  const handleAutoGenerate = async () => {
    try {
      setLoading(true);

      const res = await fetch(REVIEWS_GENERATE_REPLY_BY_AI, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review: row,
          name: row?.reviewer?.displayName,
          singlePerson: clientInfo?.singlePerson
        })
      });

      const msg = await res.text();
      if (msg) handleEditMessage(msg);
    } catch (err) {
      console.error('Error generating message', err);
    } finally {
      setLoading(false);
    }
  };

  if (editable) {
    return (
      <div className="relative">
        <textarea
          className="w-full p-2 text-sm border rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Edit the review answer message here"
          disabled={loading}
          value={reviews.editable.message || ''}
          onChange={(e) => handleEditMessage(e.target.value)}
        />

        {row?.rev_comment && (
          <button
            type="button"
            onClick={handleAutoGenerate}
            disabled={loading}
            className="absolute bottom-2 right-2 bg-blue-100 hover:bg-blue-200 rounded-full p-2 transition-colors"
            title="Auto Generate">
            <svg
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              style={{
                animation: loading ? 'spin 1s linear infinite' : 'none',
                transform: 'rotate(310deg)'
              }}>
              <path
                d="m16 0c8.836556 0 16 7.163444 16 16s-7.163444 16-16 16-16-7.163444-16-16 7.163444-16 16-16zm7.5013519 8h-5c-.5522847 0-1 .44771525-1 1v5c0 .5522847.4477153 1 1 1 .5522848 0 1-.4477153 1-1l.0011164-2.872584c1.5476338 1.1131888 2.4975317 2.9096298 2.4975317 4.872584 0 3.3137085-2.6862915 6-6 6s-6-2.6862915-6-6c0-2.6791615 1.771854-5.0121287 4.3028795-5.7568853.5298238-.1559011.832948-.71179106.6770468-1.24161488-.1559011-.52982382-.711791-.83294803-1.2416148-.67704688-3.3764809.99353263-5.7383115 4.10331096-5.7383115 7.67554706 0 4.418278 3.581722 8 8 8s8-3.581722 8-8c0-2.3423774-1.015734-4.5083081-2.7082286-6.00072306l2.2095805.00072306c.5522848 0 1-.44771525 1-1s-.4477152-1-1-1z"
                fill="#3766a8"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <TruncatedText
      text={cell}
      maxLength={60}
      className="text-sm text-muted-foreground"
    />
  );
};

const AllReviews = () => {
  const dispatch = useDispatch();
  const { customerList } = useSelector(clientsSelector);

  const toBool = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' || normalized === '1' || normalized === 'yes'
      );
    }
    return false;
  };

  const isRejectedReview = (row) => toBool(row?.rejected);
  const isApprovedReview = (row) =>
    toBool(row?.checked) && !isRejectedReview(row);
  const isPendingReview = (row) =>
    !isApprovedReview(row) && !isRejectedReview(row);
  const getReviewRating = (row) =>
    showGoogleReviews
      ? textRatingToNumber(row?.starRating)
      : Number(row?.starRating);
  const getReviewCreatedUnix = (row) => {
    const raw = row?.createdTimeUnix;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const [showReplied, setShowReplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [showGoogleReviews] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [selectedAccountManager, setSelectedAccountManager] = useState('all');
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  // helpers de fechas
  function getDaysAgoUnix() {
    const today = new Date();
    today.setDate(today.getDate() - 7);
    return Math.floor(today.getTime());
  }
  function getCurrentUnix() {
    return Math.floor(new Date().getTime());
  }
  function formatDateToInput(unixTimestamp) {
    const date = new Date(unixTimestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const [dateFrom, setDateFrom] = useState(formatDateToInput(getDaysAgoUnix()));
  const [dateTo, setDateTo] = useState(formatDateToInput(getCurrentUnix()));
  const [starsFilter, setStarsFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const reviews = useSelector((state) => state?.reviews?.reviews);

  const setStateReviews = (reviewsArr) => {
    dispatch({ type: 'reviews/setReviews', payload: reviewsArr });
  };

  const handleApplyFilters = () => {
    const filters = {};

    // Apply date filters
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filters.dateFrom = Math.floor(fromDate.getTime() / 1000); // Convert to Unix timestamp
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filters.dateTo = Math.floor(toDate.getTime() / 1000); // Convert to Unix timestamp
    }

    // Apply stars filter
    if (starsFilter !== 'all') {
      filters.starRating = starsFilter;
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'approved') {
        filters.checked = true;
      } else if (statusFilter === 'pending') {
        filters.checked = false;
      } else if (statusFilter === 'rejected') {
        filters.rejected = true;
      }
    }

    // Apply Account Manager filter
    if (selectedAccountManager !== 'all') {
      filters.accountOwner = selectedAccountManager;
    }

    setFilter(filters);
  };

  // Create clientsMap for easy lookup
  const clientsMap = React.useMemo(() => {
    const map = {};
    if (customerList && customerList.length > 0) {
      customerList.forEach((client) => {
        if (client.zohoId) {
          map[client.zohoId] = client;
        }
        if (client.socialnetworks?.googleMyBusinessLocationName) {
          map[client.socialnetworks.googleMyBusinessLocationName] = client;
        }
      });
    }
    return map;
  }, [customerList]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getReviews = async (queryParams) => {
    setStateReviews([]);
    setLoading(true);
    dispatch(showLoading());

    const url = showGoogleReviews
      ? API_GET_GOOGLE_REVIEWS_V2
      : API_GET_YELP_REVIEWS;

    axios
      .get(url, { params: { ...queryParams } })
      .then((res) => {
        let data = res.data;

        data = data.map((item) => {
          item.id = item.reviewId;

          const manager = accountManagers.find(
            (m) => m.id === item.accountOwner
          );

          if (manager) item.assignedTo = manager.full_name;

          // Add client info to each review
          let clientInfo = null;
          if (item.locationName && clientsMap[item.locationName]) {
            clientInfo = clientsMap[item.locationName];
          } else if (item.zohoId && clientsMap[item.zohoId]) {
            clientInfo = clientsMap[item.zohoId];
          }

          if (clientInfo) {
            item.clientName = clientInfo.name || clientInfo.businessName;
            item.clientInfo = clientInfo;
          }

          return item;
        });

        // Sort by date
        const sortedData = data.sort(
          (a, b) => b.createdTimeUnix - a.createdTimeUnix
        );

        // Apply complete client-side filtering so all filters compose correctly,
        // even when backend params are partially ignored.
        const filteredData = sortedData.filter((item) => {
          if (
            queryParams?.accountOwner &&
            String(item?.accountOwner) !== String(queryParams.accountOwner)
          ) {
            return false;
          }

          if (
            queryParams?.starRating !== undefined &&
            queryParams?.starRating !== null &&
            queryParams?.starRating !== 'all'
          ) {
            const rating = Number(getReviewRating(item));
            const selectedRating = Number(queryParams.starRating);
            if (Number.isNaN(rating) || rating !== selectedRating) return false;
          }

          if (queryParams?.dateFrom) {
            const createdUnix = getReviewCreatedUnix(item);
            if (createdUnix < Number(queryParams.dateFrom)) return false;
          }

          if (queryParams?.dateTo) {
            const createdUnix = getReviewCreatedUnix(item);
            if (createdUnix > Number(queryParams.dateTo)) return false;
          }

          if (queryParams?.rejected && !isRejectedReview(item)) {
            return false;
          }

          if (queryParams?.checked === true && !isApprovedReview(item)) {
            return false;
          }

          if (queryParams?.checked === false && !isPendingReview(item)) {
            return false;
          }

          return true;
        });

        setStateReviews(filteredData);
        dispatch(hideLoading());
        setLoading(false);
        setInitialLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching reviews:', error);
        setStateReviews([]);
        dispatch(hideLoading());
        setLoading(false);
        setInitialLoading(false);
      });
  };

  // const renderStars = (count) => (
  //   <div className="flex gap-0.5">
  //     {[1, 2, 3, 4, 5].map((star) => (
  //       <Star
  //         key={star}
  //         className={`h-5 w-5 ${star <= count
  //           ? 'fill-yellow-400 text-yellow-400'
  //           : 'fill-muted text-muted'
  //           }`}
  //       />
  //     ))}
  //   </div>
  // );

  const DateFormat = (cell, row) => {
    if (cell && !row.rejected) {
      if (showGoogleReviews) {
        return (
          <span className="text-sm">
            {new Date(cell * 1000).toLocaleDateString()}
          </span>
        );
      }
      return (
        <span className="text-sm">{new Date(cell).toLocaleDateString()}</span>
      );
    }
    return <span className="text-sm">--</span>;
  };

  useEffect(() => {
    getReviews({ replied: showReplied, ...filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showReplied, showGoogleReviews, refresh]);

  const [expandedRows] = useState([]);

  const reviewsSlice = useSelector((state) => state.reviews);

  const dataWithFlags = (reviews || []).map((row) => ({
    ...row,
    _expanded: expandedRows.includes(row.id),
    _isEditing:
      reviewsSlice.editable.editable && reviewsSlice.editable.id === row.id
  }));

  // Use loading state for skeleton (both initial and filtering)
  const isLoadingData = loading || initialLoading;

  const handleSelectOne = (checked, id) => {
    if (checked) {
      setSelectedReviews((prev) => [...prev, id]);
    } else {
      setSelectedReviews((prev) => prev.filter((rid) => rid !== id));
    }
  };

  const handleBulkApprove = async () => {
    const toApprove = dataWithFlags.filter(
      (r) => selectedReviews.includes(r.id) && !r.checked
    );

    if (toApprove.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to approve',
        text: 'All selected reviews are already approved.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    setIsBulkApproving(true);
    dispatch(showLoading());

    const results = await Promise.allSettled(
      toApprove.map((r) =>
        axios.post(`${API_REVIEW_APPROVE}/`, { id: r.reviewId })
      )
    );

    const allApprovedIds = toApprove
      .filter(
        (_, i) =>
          results[i]?.status === 'fulfilled' && results[i].value?.status === 200
      )
      .map((r) => r.id);

    if (allApprovedIds.length > 0) {
      dispatch({
        type: 'reviews/approve',
        payload: (reviews || []).map((r) =>
          allApprovedIds.includes(r.id) ? { ...r, checked: true } : r
        )
      });
    }

    setSelectedReviews((prev) =>
      prev.filter((id) => !allApprovedIds.includes(id))
    );
    setIsBulkApproving(false);
    dispatch(hideLoading());

    const totalApproved = allApprovedIds.length;
    const totalRequested = toApprove.length;
    if (totalApproved === totalRequested) {
      Swal.fire({
        icon: 'success',
        title: 'Approved!',
        text: `${totalApproved} review${
          totalApproved !== 1 ? 's' : ''
        } approved successfully.`,
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Partial Success',
        text: `${totalApproved} of ${totalRequested} review(s) approved.`
      });
    }
  };

  const renderStars = (count) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= count
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-muted text-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  const getStatusBadge = (row) => {
    if (isPendingReview(row)) {
      return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    } else if (isRejectedReview(row)) {
      return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
    }
  };

  const renderMobileReviewCard = (review) => {
    const rating = showGoogleReviews
      ? textRatingToNumber(review.starRating)
      : review.starRating;

    return (
      <div
        key={review.id}
        className={`border-b border-border last:border-b-0 p-4 space-y-3 ${
          selectedReviews.includes(review.id) ? 'bg-primary/5' : ''
        }`}>
        {/* Header: Avatar + Name + Status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={selectedReviews.includes(review.id)}
              onCheckedChange={(checked) => handleSelectOne(checked, review.id)}
              className="shrink-0"
              aria-label={`Select review from ${review.reviewer?.displayName}`}
            />
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">
                {review.reviewer?.displayName}
              </p>
              <p className="text-xs text-muted-foreground">
                {review.clientName || review.locationName || 'Unknown Client'} ·{' '}
                {DateFormat(review.createdTimeUnix, review)}
              </p>
            </div>
          </div>
          {getStatusBadge(review)}
        </div>

        {/* Stars */}
        {renderStars(rating)}

        {/* Comment */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Comment
          </p>
          <p className="text-sm text-foreground">{review.rev_comment}</p>
        </div>

        {/* Response */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Response
          </p>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {review.msgReply || 'No response yet'}
          </p>
        </div>

        {/* Scheduled */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          Scheduled: {DateFormat(review.schedule, review)}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-xs gap-1.5 flex-1">
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-xs gap-1.5 flex-1 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700">
            <Check className="h-3.5 w-3.5" />
            Approve
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-xs gap-1.5 flex-1 text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Main Content */}
      <div className="flex-1 min-w-0 overflow-x-hidden">
        {/* Content Area */}
        <main className="p-3 sm:p-6 lg:p-10 space-y-4 sm:space-y-8">
          {/* Welcome Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 sm:mb-8">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-foreground">
                All Clients Google Reviews
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                See all reviews across all clients
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search"
                  className="pl-12 h-12 sm:h-14 w-full sm:w-72 rounded-xl text-base"
                />
              </div>
            </div>
          </div>

          {/* Compact Filters */}

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span>Filters:</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] lg:grid-cols-[145px_auto_145px] gap-2 sm:gap-3 items-center lg:w-auto">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-[130px] sm:w-36 rounded-lg text-xs sm:text-sm"
              />
              <span className="text-muted-foreground text-sm text-center">
                to
              </span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-[130px] sm:w-36 rounded-lg text-xs sm:text-sm"
              />
            </div>
            <Select value={starsFilter} onValueChange={setStarsFilter}>
              <SelectTrigger className="h-9 w-28 sm:w-32 rounded-lg text-xs sm:text-sm">
                <SelectValue placeholder="Rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-28 sm:w-32 rounded-lg text-xs sm:text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleApplyFilters}
              className="h-9 px-4 text-xs sm:text-sm gap-2 text-white bg-primary hover:bg-primary/90 rounded-lg shadow-sm">
              <Filter className="h-3.5 w-3.5" />
              Filter
            </Button>
            <Select
              value={selectedAccountManager}
              onValueChange={setSelectedAccountManager}>
              <SelectTrigger className="h-9 w-28 sm:w-32 rounded-lg text-xs sm:text-sm">
                <SelectValue placeholder="All Managers">
                  {selectedAccountManager === 'all'
                    ? 'All Managers'
                    : accountManagers.find(
                        (m) => m.id === selectedAccountManager
                      )?.full_name || 'All Managers'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Managers</SelectItem>
                {accountManagers
                  .filter((manager) => manager.full_name !== 'All')
                  .map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      <span className="truncate">{manager.full_name}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant={showReplied ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowReplied(!showReplied)}
              className="h-9 px-4 text-xs sm:text-sm gap-2 rounded-lg shadow-sm">
              {showReplied ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
              {showReplied ? 'Hide Replied' : 'Show Replied'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilter(null);
                setDateFrom(formatDateToInput(getDaysAgoUnix()));
                setDateTo(formatDateToInput(getCurrentUnix()));
                setStarsFilter('all');
                setStatusFilter('all');
                setSelectedAccountManager('all');
                setShowReplied(false);
              }}
              className="h-9 px-3 text-xs sm:text-sm gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
          <Card className="rounded-2xl border-2 border-border shadow-sm overflow-hidden">
            <CardHeader className="pb-4 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-base sm:text-lg font-semibold">
                  Recent Reviews {reviews?.length > 0 && `(${reviews.length})`}
                </CardTitle>
                {selectedReviews.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {selectedReviews.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelectedReviews(dataWithFlags.map((r) => r.id))
                      }
                      className="h-8 px-2 text-sm">
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedReviews([])}
                      className="h-8 px-2 text-sm">
                      Deselect All
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBulkApprove}
                      disabled={isBulkApproving}
                      className="h-8 px-3 text-sm bg-green-600 hover:bg-green-700 text-white gap-1.5">
                      {isBulkApproving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCheck className="h-3.5 w-3.5" />
                      )}
                      Approve Selected
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Reviews Table */}
              {isLoadingData ? (
                <div className="p-6 space-y-4">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 pb-4 border-b border-border last:border-0">
                      <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <div className="flex gap-2">
                        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                        <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : dataWithFlags.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No Reviews
                </div>
              ) : (
                <>
                  <div className="block sm:hidden">
                    {dataWithFlags.map(renderMobileReviewCard)}
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-12 text-center py-4 px-2 border-r-0">
                            <Checkbox
                              checked={
                                dataWithFlags.length > 0 &&
                                selectedReviews.length === dataWithFlags.length
                                  ? true
                                  : selectedReviews.length > 0
                                  ? 'indeterminate'
                                  : false
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedReviews(
                                    dataWithFlags.map((r) => r.id)
                                  );
                                } else {
                                  setSelectedReviews([]);
                                }
                              }}
                              aria-label="Select all"
                            />
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 px-5 border-r-0">
                            Client
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 px-5 border-r-0">
                            Reviewer
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 border-r-0">
                            Rating
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 min-w-[200px] border-r-0">
                            Comment
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 min-w-[220px] border-r-0">
                            Your Response
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 border-r-0">
                            Scheduled
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 border-r-0">
                            Status
                          </TableHead>
                          <TableHead className="text-base font-semibold py-4 px-5">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dataWithFlags.map((review) => {
                          const rating = showGoogleReviews
                            ? textRatingToNumber(review.starRating)
                            : review.starRating;

                          return (
                            <TableRow
                              key={review.id}
                              className={`align-top hover:bg-muted/30 ${
                                selectedReviews.includes(review.id)
                                  ? 'bg-primary/5'
                                  : ''
                              }`}>
                              <TableCell className="py-5 px-2 text-center border-r-0">
                                <Checkbox
                                  checked={selectedReviews.includes(review.id)}
                                  onCheckedChange={(checked) =>
                                    handleSelectOne(checked, review.id)
                                  }
                                  aria-label={`Select review from ${review.reviewer?.displayName}`}
                                />
                              </TableCell>
                              <TableCell className="py-5 px-5 border-r-0">
                                <div className="flex items-center gap-3">
                                  <div>
                                    <p className="text-base leading-tight mb-0">
                                      {review.clientName ||
                                        review.locationName ||
                                        'Unknown Client'}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-5 px-5 border-r-0">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: '#3766A820' }}>
                                    <User
                                      className="h-5 w-5"
                                      style={{ color: '#3766A8' }}
                                    />
                                  </div>
                                  <div>
                                    <p className="font-medium text-base leading-tight mb-0">
                                      {review.reviewer?.displayName}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {DateFormat(
                                        review.createdTimeUnix,
                                        review
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-5 border-r-0">
                                {renderStars(rating)}
                              </TableCell>
                              <TableCell
                                className="py-5 border-r-0"
                                style={{
                                  minWidth: '200px',
                                  maxWidth: '300px',
                                  verticalAlign: 'top'
                                }}>
                                <TruncatedText
                                  text={review.rev_comment}
                                  maxLength={60}
                                />
                              </TableCell>
                              <TableCell
                                className="py-5 border-r-0"
                                style={{
                                  minWidth: '220px',
                                  maxWidth: '320px',
                                  verticalAlign: 'top'
                                }}>
                                <Answer
                                  cell={review.msgReply}
                                  row={review}
                                  clientInfo={review.clientInfo}
                                />
                              </TableCell>
                              <TableCell className="py-5 border-r-0">
                                <div className="flex items-center gap-2 text-base">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  {DateFormat(review.schedule, review)}
                                </div>
                              </TableCell>
                              <TableCell className="py-5 border-r-0">
                                {getStatusBadge(review)}
                              </TableCell>
                              <TableCell className="py-5 px-5">
                                <Actions
                                  row={review}
                                  refresh={() => setRefresh(!refresh)}
                                  setLoading={setLoading}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Mantengo esto porque en Answer se usa animation: 'spin ...' */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(310deg); }
          to { transform: rotate(670deg); }
        }
        
        /* Force button border colors and width */
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-check) {
          border-color: #86efac !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-trash-2) {
          border-color: #fca5a5 !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-pencil) {
          border-color: #cbd5e1 !important;
          border-width: 0.5px !important;
        }
      `}</style>
    </>
  );
};

export default AllReviews;
